import React, { createContext, useContext, useState, useCallback } from 'react';

// Whatever the user is currently looking at. TableView (and eventually other
// views) publishes its state here so the Ada sidebar always knows what's on
// screen without needing to thread props through every component.
// Radius filters can be any combination of the three independent types.
// `null` for any inactive type. Distance is always in miles (the unit the
// rest of the app exposes).
export type AdaRadiusContext = {
  candidateLat?: number;
  candidateLng?: number;
  candidateDistance?: number;
  managerLat?: number;
  managerLng?: number;
  managerDistance?: number;
  companyLat?: number;
  companyLng?: number;
  companyDistance?: number;
} | null;

// Actions exposed back from TableView so the Ada sidebar can apply Claude's
// proposed actions (filters / tabs / radius / search) without prop drilling.
// Each handler matches the existing TableView setter or workflow function.
// The default values are no-ops so the context is safe to consume even
// before TableView has wired itself in (e.g. user is on a non-table view).
//
// SAFETY: every action mutates frontend state only — never the database.
// Adding a new action that touches the DB would require revisiting the
// Ada agentic safety boundary (see backend/services/ada/tools/index.js).
export type AdaActions = {
  applyFilterGroups: (groups: any[], mode?: 'replace' | 'add') => void;
  switchTab: (tab: 'companies' | 'candidates' | 'managers') => void;
  applyRadiusSearch: (args: {
    type: 'candidate' | 'company' | 'manager';
    location: string;
    distance: number;
  }) => Promise<void> | void;
  setKeywordSearch: (term: string) => void;
  // Dedicated employment-type multi-select picker. Distinct from filter
  // groups because the picker is populated from real project values and
  // users select from a checklist — Ada must do the same.
  setEmploymentTypes: (types: string[]) => void;
  // Launch the AI Smart Search preview modal for a natural-language query.
  // Resolves when the user closes the modal — either by applying the
  // (possibly edited) filter groups, or by cancelling. The promise carries
  // back the result so Ada can ack with the correct continuation.
  runSmartSearch: (query: string) => Promise<{
    applied: boolean;
    rowCount?: number;
    keywordsApplied?: string[];
    error?: string;
  }>;
};

export type AdaContextValue = {
  projectId: number | null;
  projectName: string | null;
  // 'companies' | 'candidates' | 'managers' | null when not on a table view
  dataType: 'companies' | 'candidates' | 'managers' | null;
  filterGroups: any[];
  // When the user clicked "candidates at Company X" from the companies tab,
  // the table applies a junction-table filter that isn't captured in
  // filterGroups. Ada needs to see this so its aggregations match the table.
  associatedCompany: string | null;
  // The other on-screen filter signals — published by TableView so Ada
  // always sees what's actually applied.
  searchTerm: string | null;
  employmentTypes: string[];
  radius: AdaRadiusContext;
  // Optional readout used in the context indicator (e.g. "1,247 companies").
  visibleTotal: number | null;
  // Action handlers wired by TableView. Used by the chat sidebar to apply
  // Claude's proposed actions when the user clicks Apply on an action card.
  actions: AdaActions;
};

type AdaContextShape = AdaContextValue & {
  setAdaContext: (next: Partial<AdaContextValue>) => void;
};

const noopActions: AdaActions = {
  applyFilterGroups: () => {},
  switchTab: () => {},
  applyRadiusSearch: () => {},
  setKeywordSearch: () => {},
  setEmploymentTypes: () => {},
  runSmartSearch: async () => ({ applied: false, error: 'Smart search not available in this view' }),
};

const defaultValue: AdaContextValue = {
  projectId: null,
  projectName: null,
  dataType: null,
  filterGroups: [],
  associatedCompany: null,
  searchTerm: null,
  employmentTypes: [],
  radius: null,
  visibleTotal: null,
  actions: noopActions,
};

const Ctx = createContext<AdaContextShape>({
  ...defaultValue,
  setAdaContext: () => {},
});

export function AdaContextProvider({ children }: { children: React.ReactNode }) {
  const [value, setValue] = useState<AdaContextValue>(defaultValue);
  const setAdaContext = useCallback(
    (next: Partial<AdaContextValue>) => setValue((prev) => ({ ...prev, ...next })),
    []
  );
  return (
    <Ctx.Provider value={{ ...value, setAdaContext }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAdaContext() {
  return useContext(Ctx);
}
